/**
 * /admin/reports/gst — GST register, verification & returns (GSTR-1 / 3B).
 * @file src/web/features/admin/pages/reports/gst.tsx
 *
 * Four views, all auto-populated from the imported vouchers:
 * - Sales / Purchases registers (per-document, with validation issues)
 * - GSTR-1 (section-wise summary + portal JSON download)
 * - GSTR-3B (table 3.1 outward + table 4 ITC + net payable)
 * Sales & purchases are separate so each is verified on its own before filing.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, FileText, Download, ShieldAlert, GitCompare, CheckCircle2 } from 'lucide-react';
import { toast } from '@bloomneo/uikit';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { FileGstr1Panel } from '../components/FileGstr1Panel';
import { PageLoading } from '../components/PageLoading';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { useCompany } from '../lib/company-context';
import { GstinScope, CrossCheckPanel, HsnSummary, DocIssueSummary, ManualEntryPanel } from '../components/GstScopePanels';
import { usePeriod } from '../lib/period-context';
import { PeriodBar } from '../components/PeriodBar';

type Direction = 'outward' | 'inward';
type View = Direction | 'gstr1' | 'gstr3b' | 'recon2b';

interface Issue { code: string; message: string; severity: 'error' | 'warning' }
interface GstDoc {
  voucherId: string; number: string | null; date: string; type: string; isNote: boolean;
  partyName: string | null; partyGstin: string | null; partyLedgerId: string | null; itemLedgerId: string | null; placeOfSupply: string | null; hsn: string | null;
  taxableValue: number; cgst: number; sgst: number; igst: number; cess: number; totalTax: number; invoiceValue: number;
  rate: number | null; interState: boolean; supplyType: string; issues: Issue[];
}
interface Summary {
  count: number; taxableValue: number; cgst: number; sgst: number; igst: number; cess: number; totalTax: number; invoiceValue: number; withIssues: number;
  byRate: { rate: number | null; taxableValue: number; tax: number; count: number }[];
  bySupplyType: { supplyType: string; count: number; taxableValue: number; tax: number }[];
}
interface MissingInfoRow { code: string; label: string; severity: 'error' | 'warning'; count: number; sample: { voucherId: string; number: string | null; partyName: string | null }[] }
interface Changes { hasBaseline: boolean; baselineAt?: string; baselineDocCount?: number; added: string[]; removed: string[]; changed: string[]; unchanged: number }
interface RegisterResp { direction: Direction; companyGstin: string | null; summary: Summary; missingInfo: MissingInfoRow[]; changes: Changes; docs: GstDoc[] }
interface TaxLine { taxableValue: number; igst: number; cgst: number; sgst: number; cess: number }
interface Returns {
  gstin: string | null;
  registration?: { id: string; gstin: string; stateName: string | null } | null;
  multiRegistration?: boolean;
  unattributedCount?: number;
  unknownHsnCodes?: string[];
  unassignedDocuments?: number;
  gstr1: { fp: string; gstin: string | null; summary: { totalDocs: number; taxableValue: number; totalTax: number; invoiceValue: number; sections: { code: string; label: string; count: number; taxableValue: number; tax: number }[] }; portal: any; crossCheck?: { ok: boolean; tolerance: number; rows: { field: string; sections: number; hsnSummary: number; diff: number }[] } };
  gstr3b: { fp: string; gstin: string | null; outward: { taxableOutward: TaxLine; zeroRated: TaxLine; nilExempt: TaxLine }; itc: TaxLine; netTaxPayable: TaxLine; portal?: any; needsManualEntry?: { field: string; label: string; why: string }[] };
}

const inr = (n: number) => (n ? n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

function downloadJson(name: string, obj: unknown) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function GstPage() {
  const { entityId } = useCompany();
  const { from, to } = usePeriod();
  const [view, setView] = useState<View>('outward');
  const [reg, setReg] = useState<Record<Direction, RegisterResp | null>>({ outward: null, inward: null });
  const [returns, setReturns] = useState<Returns | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Which GSTIN is in scope. Null = the entity's primary; only meaningful for a
  // client registered in more than one state.
  const [regId, setRegId] = useState<string | null>(null);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const [recon, setRecon] = useState<Recon | null>(null);

  // Registers (sales/purchases)
  useEffect(() => { setReg({ outward: null, inward: null }); setReturns(null); setRecon(null); }, [entityId, from, to, regId]);
  useEffect(() => {
    if (view !== 'outward' && view !== 'inward') return;
    if (reg[view]) return;
    let cancel = false; setError(null);
    adminFetchJson<RegisterResp>('/api/gst/register', { query: { direction: view, entityId: entityId ?? undefined, gstRegistrationId: regId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setReg((r) => ({ ...r, [view]: d })))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, from, to, regId, reg]);

  // Returns (GSTR-1 / 3B) — one fetch serves both
  useEffect(() => {
    if (view !== 'gstr1' && view !== 'gstr3b') return;
    if (returns) return;
    let cancel = false; setError(null);
    adminFetchJson<Returns>('/api/gst/returns', { query: { entityId: entityId ?? undefined, gstRegistrationId: regId ?? undefined, from: from || undefined, to: to || undefined } })
      .then((d) => !cancel && setReturns(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, from, to, regId, returns]);

  // GSTR-2B reconciliation. Period comes from the selected range's end date —
  // 2B is published per return period, so a range spanning two months would
  // be reconciled against whichever one `to` lands in, and that is stated in
  // the header rather than silently assumed.
  const period2b = to ? to.slice(5, 7) + to.slice(0, 4) : '';
  useEffect(() => {
    if (view !== 'recon2b' || recon || !period2b) return;
    let cancel = false; setError(null);
    adminFetchJson<Recon>('/api/gst/2b/reconcile', { query: { entityId: entityId ?? undefined, gstRegistrationId: regId ?? undefined, from: from || undefined, to: to || undefined, period: period2b } })
      .then((d) => !cancel && setRecon(d))
      .catch((e) => !cancel && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => { cancel = true; };
  }, [view, entityId, from, to, regId, recon, period2b]);

  const isReg = view === 'outward' || view === 'inward';
  const data = isReg ? reg[view as Direction] : null;
  const rows = useMemo(() => (data ? (onlyIssues ? data.docs.filter((d) => d.issues.length) : data.docs) : []), [data, onlyIssues]);

  return (
    <>
      <SEO title="GST" />
      <AdminPageHeader title="GST" breadcrumbs={[{ label: 'oddits' }, { label: 'GST' }]} />
      <PeriodBar />
      <GstinScope entityId={entityId ?? null} value={regId} onChange={setRegId} />

      <div className="mb-5 inline-flex flex-wrap rounded-xl border border-ink-200 bg-white p-1">
        <Tab active={view === 'outward'} onClick={() => setView('outward')} icon={ArrowUpRight} label="Sales" />
        <Tab active={view === 'inward'} onClick={() => setView('inward')} icon={ArrowDownLeft} label="Purchases" />
        <Tab active={view === 'gstr1'} onClick={() => setView('gstr1')} icon={FileText} label="GSTR-1" />
        <Tab active={view === 'recon2b'} onClick={() => setView('recon2b')} icon={GitCompare} label="2B recon" />
        <Tab active={view === 'gstr3b'} onClick={() => setView('gstr3b')} icon={FileText} label="GSTR-3B" />
      </div>

      {error && <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {error}</div>}

      {/* Registers */}
      {isReg && !data && !error && <PageLoading label="Building GST register" />}
      {isReg && data && <RegisterView view={view as Direction} data={data} rows={rows} onlyIssues={onlyIssues} setOnlyIssues={setOnlyIssues} entityId={entityId ?? null} onChanged={() => setReg((r) => ({ ...r, [view]: null }))} />}

      {/* Returns */}
      {!isReg && !returns && !error && <PageLoading label="Preparing return" />}
      {view === 'gstr1' && returns && <Gstr1View r={returns} entityId={entityId ?? null} />}
      {view === 'gstr3b' && returns && <Gstr3bView r={returns} entityId={entityId ?? null} />}

      {view === 'recon2b' && !recon && !error && <PageLoading label="Reconciling against GSTR-2B" />}
      {view === 'recon2b' && recon && <Recon2bView r={recon} period={period2b} />}
    </>
  );
}

/* ── GSTR-2B reconciliation ─────────────────────────────────────────────── */

type MatchStatus = 'matched' | 'mismatch' | 'missing_in_2b' | 'missing_in_books' | 'itc_blocked';
interface ReconMoney { taxableValue: number; igst: number; cgst: number; sgst: number; cess: number; tax: number }
interface ReconLine {
  status: MatchStatus; supplierGstin: string | null; supplierName: string | null;
  invoiceNumber: string | null; invoiceDate: string | null;
  books: ReconMoney | null; portal: ReconMoney | null; taxDifference: number;
  supplierFiledOn: string | null; itcReason: string | null; note: string | null;
}
interface Recon {
  period: string; hasPortalData: boolean;
  summary: {
    booksTotal: number; portalTotal: number; claimable: number; atRisk: number; blocked: number;
    counts: { matched: number; mismatch: number; missingIn2b: number; missingInBooks: number; itcBlocked: number; total: number };
  };
  lines: ReconLine[];
}

const STATUS_LABEL: Record<MatchStatus, string> = {
  matched: 'Matched',
  mismatch: 'Amount mismatch',
  missing_in_2b: 'Not in 2B',
  missing_in_books: 'Not in books',
  itc_blocked: 'ITC blocked',
};
const STATUS_TONE: Record<MatchStatus, string> = {
  matched: 'bg-ok-50 text-ok-700 border-ok-200',
  mismatch: 'bg-warn-50 text-warn-700 border-warn-200',
  missing_in_2b: 'bg-danger-50 text-danger-700 border-danger-200',
  missing_in_books: 'bg-ink-100 text-ink-700 border-ink-200',
  itc_blocked: 'bg-danger-50 text-danger-700 border-danger-200',
};

function Recon2bView({ r, period }: { r: Recon; period: string }) {
  const s = r.summary;

  if (!r.hasPortalData) {
    return (
      <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-14 text-center">
        <p className="text-sm font-medium text-ink-700">No GSTR-2B loaded for {period || 'this period'}.</p>
        <p className="mx-auto mt-2 max-w-lg text-xs text-ink-500">
          2B is generated by GSTN from what your suppliers filed. Load the period&rsquo;s 2B
          (POST /api/gst/2b/import?period={period}) and this screen will show which of your
          purchase credits are actually claimable.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="ITC claimable" value={`₹${inr(s.claimable)}`} sub={`${s.counts.matched} matched invoices`} tone="ok" />
        <Tile label="ITC at risk" value={`₹${inr(s.atRisk)}`} sub={s.counts.missingIn2b ? `${s.counts.missingIn2b} not declared by supplier` : 'all declared'} tone={s.atRisk > 0 ? 'warn' : undefined} />
        <Tile label="ITC blocked" value={`₹${inr(s.blocked)}`} sub={`${s.counts.itcBlocked} denied by GSTN`} tone={s.blocked > 0 ? 'warn' : undefined} />
        <Tile label="Books say paid" value={`₹${inr(s.booksTotal)}`} sub="purchase register total" />
      </div>

      {s.atRisk > 0 && (
        <div className="flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50/70 px-4 py-3 text-sm text-danger-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            ₹{inr(s.atRisk)} of input credit is in your books but not in 2B. Claiming it on the
            GSTR-3B is reversible later with interest — chase the supplier to file, or leave it out
            of this period&rsquo;s claim.
          </span>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
        <table className="w-full min-w-[52rem] text-sm">
          <thead>
            <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
              <th className="px-5 py-3 font-medium">Status</th>
              <th className="px-5 py-3 font-medium">Invoice</th>
              <th className="px-5 py-3 font-medium">Supplier</th>
              <th className="px-5 py-3 text-right font-medium">Books tax</th>
              <th className="px-5 py-3 text-right font-medium">2B tax</th>
              <th className="px-5 py-3 text-right font-medium">Difference</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {r.lines.map((l, i) => (
              <tr key={i} className="align-top hover:bg-ink-50/60">
                <td className="px-5 py-3">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[11px] font-medium ${STATUS_TONE[l.status]}`}>
                    {STATUS_LABEL[l.status]}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <span className="font-medium text-ink-900">{l.invoiceNumber ?? '—'}</span>
                  {l.note && <span className="block max-w-md text-xs text-ink-500">{l.note}</span>}
                </td>
                <td className="px-5 py-3">
                  <span className="text-ink-700">{l.supplierName ?? '—'}</span>
                  {l.supplierGstin && <span className="block font-mono text-[11px] text-ink-400">{l.supplierGstin}</span>}
                  {l.supplierFiledOn && <span className="block text-[11px] text-ink-400">filed {l.supplierFiledOn}</span>}
                </td>
                <td className="px-5 py-3 text-right tabular-nums text-ink-800">{l.books ? inr(l.books.tax) : '—'}</td>
                <td className="px-5 py-3 text-right tabular-nums text-ink-800">{l.portal ? inr(l.portal.tax) : '—'}</td>
                <td className={`px-5 py-3 text-right tabular-nums ${l.taxDifference ? 'font-medium text-danger-700' : 'text-ink-400'}`}>
                  {l.taxDifference ? inr(l.taxDifference) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Register view ──────────────────────────────────────────────────────── */

function RegisterView({ view, data, rows, onlyIssues, setOnlyIssues, entityId, onChanged }: { view: Direction; data: RegisterResp; rows: GstDoc[]; onlyIssues: boolean; setOnlyIssues: (b: boolean) => void; entityId: string | null; onChanged: () => void }) {
  const s = data.summary;
  if (s.count === 0) return <Empty label={view === 'outward' ? 'sales' : 'purchase'} />;
  return (
    <div className="space-y-6">
      {!data.companyGstin && (
        <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-warn-200 bg-warn-50/60 px-4 py-3 text-sm text-warn-700">
          <ShieldAlert className="h-5 w-5 shrink-0 text-warn-600" />
          <span className="flex-1">This entity has no GSTIN set — needed on the return.</span>
          <Link to="/admin/masters" className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-warn-700 no-underline ring-1 ring-warn-300 hover:bg-warn-100">Set it in Masters → Company</Link>
        </div>
      )}
      <ValidationBar direction={view} entityId={entityId} changes={data.changes} onSaved={onChanged} />
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Taxable value" value={`₹${inr(s.taxableValue)}`} sub={`${s.count} documents`} />
        <Tile label="Total tax" value={`₹${inr(s.totalTax)}`} sub={`CGST ${inr(s.cgst)} · SGST ${inr(s.sgst)} · IGST ${inr(s.igst)}`} />
        <Tile label="Invoice value" value={`₹${inr(s.invoiceValue)}`} sub="Taxable + tax" />
        <Tile label="Needs review" value={String(s.withIssues)} sub={s.withIssues ? 'documents with issues' : 'all documents valid'} tone={s.withIssues ? 'warn' : 'ok'} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Breakdown title="By tax rate" rows={s.byRate.map((r) => ({ label: r.rate == null ? '—' : `${r.rate}%`, count: r.count, taxable: r.taxableValue, tax: r.tax }))} />
        <Breakdown title="By supply type" rows={s.bySupplyType.map((t) => ({ label: t.supplyType, count: t.count, taxable: t.taxableValue, tax: t.tax }))} />
      </div>

      {data.missingInfo.length > 0 && <MissingInfo rows={data.missingInfo} onPick={() => setOnlyIssues(true)} />}

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-ink-400">{view === 'outward' ? 'Sales' : 'Purchase'} register</h2>
          <div className="flex items-center gap-4">
            <Link to="/admin/masters" className="text-xs font-medium text-inkblack no-underline hover:text-inkblack">Fix in Masters ↗</Link>
            {s.withIssues > 0 && (
              <label className="inline-flex items-center gap-2 text-xs font-medium text-ink-600">
                <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} className="rounded border-ink-300" />
                Only show {s.withIssues} needing review
              </label>
            )}
          </div>
        </div>
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">No.</th><th className="px-4 py-3 font-medium">Party</th>
                <th className="px-4 py-3 font-medium">GSTIN</th><th className="px-4 py-3 text-center font-medium">POS</th>
                <th className="px-4 py-3 text-right font-medium">Taxable</th><th className="px-4 py-3 text-right font-medium">CGST</th>
                <th className="px-4 py-3 text-right font-medium">SGST</th><th className="px-4 py-3 text-right font-medium">IGST</th>
                <th className="px-4 py-3 text-center font-medium">Rate</th><th className="px-4 py-3 font-medium">Type</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.voucherId} className={`border-b border-ink-100 last:border-0 ${d.issues.length ? 'bg-warn-50/40' : ''}`}>
                  <td className="whitespace-nowrap px-4 py-2.5 text-ink-500">{d.date}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-ink-700">{d.number ?? '—'}{d.isNote && <span className="ml-1 rounded bg-ink-100 px-1 text-[10px] text-ink-500">{d.type.includes('Credit') ? 'CN' : 'DN'}</span>}</td>
                  <td className="px-4 py-2.5">
                    <span className="text-ink-800">{d.partyName ?? '—'}</span>
                    {d.issues.length > 0 && <span className="ml-2 inline-flex items-center gap-1 align-middle text-[11px] font-medium text-warn-700" title={d.issues.map((i) => i.message).join('\n')}><AlertTriangle className="h-3 w-3" /> {d.issues.length}</span>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs text-ink-500">{d.partyGstin ?? '—'}</td>
                  <td className="px-4 py-2.5 text-center text-ink-500">{d.placeOfSupply ?? '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-800">{inr(d.taxableValue)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-500">{d.cgst ? inr(d.cgst) : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-500">{d.sgst ? inr(d.sgst) : '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-ink-500">{d.igst ? inr(d.igst) : '—'}</td>
                  <td className="px-4 py-2.5 text-center text-ink-500">{d.rate == null ? '—' : `${d.rate}%`}</td>
                  <td className="px-4 py-2.5"><span className="rounded-full bg-ink-100 px-2 py-0.5 text-[11px] font-medium text-ink-600">{d.supplyType}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Validation snapshot bar (#7) ───────────────────────────────────────── */

function ValidationBar({ direction, entityId, changes, onSaved }: { direction: Direction; entityId: string | null; changes: Changes; onSaved: () => void }) {
  const [saving, setSaving] = useState(false);
  const drift = changes.added.length + changes.removed.length + changes.changed.length;
  const noEntity = !entityId;

  async function save() {
    setSaving(true);
    try {
      const res = await adminFetch('/api/gst/validate/save', { method: 'POST', query: { direction, entityId: entityId ?? undefined } });
      const b = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(b?.message || 'Failed');
      toast.success(`Validated — ${b.docCount} documents saved as baseline`); onSaved();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to save'); }
    finally { setSaving(false); }
  }

  const tone = noEntity ? 'neutral' : !changes.hasBaseline ? 'neutral' : drift > 0 ? 'warn' : 'ok';
  const ring = tone === 'warn' ? 'border-warn-200 bg-warn-50/50' : tone === 'ok' ? 'border-ok-200 bg-ok-50/50' : 'border-ink-200 bg-white';
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border p-4 ${ring}`}>
      <div className="text-sm">
        {noEntity ? (
          <span className="text-ink-500">Select a single entity to save and track a validated baseline.</span>
        ) : !changes.hasBaseline ? (
          <span className="text-ink-600">Not yet validated. Save a baseline to track changes on future imports.</span>
        ) : drift > 0 ? (
          <span className="font-medium text-warn-700">
            {changes.changed.length} changed · {changes.added.length} new · {changes.removed.length} removed
            <span className="font-normal text-warn-600"> since last validated {changes.baselineAt ? new Date(changes.baselineAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''}</span>
          </span>
        ) : (
          <span className="font-medium text-ok-700">No changes since last validated {changes.baselineAt ? new Date(changes.baselineAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : ''} · {changes.unchanged} documents</span>
        )}
      </div>
      {!noEntity && (
        <button onClick={save} disabled={saving}
          className="rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50 disabled:opacity-50">
          {changes.hasBaseline ? 'Re-validate & save' : 'Save as validated'}
        </button>
      )}
    </div>
  );
}

/* ── Missing-Info report (#3) ───────────────────────────────────────────── */

function MissingInfo({ rows, onPick }: { rows: MissingInfoRow[]; onPick: () => void }) {
  const errors = rows.filter((r) => r.severity === 'error').reduce((s, r) => s + r.count, 0);
  const warns = rows.filter((r) => r.severity === 'warning').reduce((s, r) => s + r.count, 0);
  return (
    <div className="rounded-2xl border border-warn-200 bg-warn-50/50 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-lg bg-warn-100 text-warn-600"><AlertTriangle className="h-4 w-4" /></span>
          <div>
            <p className="text-sm font-semibold text-ink-900">Missing information</p>
            <p className="text-xs text-ink-500">{errors} error{errors !== 1 ? 's' : ''} · {warns} warning{warns !== 1 ? 's' : ''} to review before filing</p>
          </div>
        </div>
        <button onClick={onPick} className="rounded-full border border-ink-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-700 transition hover:bg-ink-50">Review flagged rows</button>
      </div>
      <div className="divide-y divide-warn-100 rounded-xl border border-warn-100 bg-white/70">
        {rows.map((r) => (
          <div key={r.code} className="flex items-center justify-between px-3.5 py-2.5">
            <span className="flex items-center gap-2 text-sm text-ink-700">
              <span className={`size-1.5 rounded-full ${r.severity === 'error' ? 'bg-danger-500' : 'bg-warn-400'}`} />
              {r.label}
            </span>
            <span className="text-xs font-semibold tabular-nums text-ink-500">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Filing preparer & tracker (Phase 4) ───────────────────────────────── */

interface Readiness { gstin: string | null; gstinMissing: boolean; blockingErrors: number; warnings: number; canFile: boolean }
interface Filing { id: string; returnType: string; period: string; status: string; taxableValue: number; totalTax: number; blockingErrors: number; warnings: number; arn: string | null; createdAt: string; filedAt: string | null }

function FilingPanel({ returnType, entityId }: { returnType: 'GSTR1' | 'GSTR3B'; entityId: string | null }) {
  // The period the page is showing. Prepare used to omit it, so the filing
  // record was written with no month — an ARN that cannot say which return it
  // belongs to. The API now refuses that, so it has to be sent.
  const { from, to } = usePeriod();
  const [preparing, setPreparing] = useState(false);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [filingId, setFilingId] = useState<string | null>(null);
  const [arn, setArn] = useState('');
  const [filings, setFilings] = useState<Filing[]>([]);

  const loadFilings = () => adminFetchJson<{ filings: Filing[] }>('/api/gst/filings', { query: { entityId: entityId ?? undefined } }).then((d) => setFilings(d.filings)).catch(() => {});
  useEffect(() => { setReadiness(null); setFilingId(null); if (entityId) loadFilings(); }, [entityId, returnType, from, to]);

  async function prepare() {
    setPreparing(true);
    try {
      const res = await adminFetch('/api/gst/file/prepare', {
        method: 'POST',
        query: { type: returnType, entityId: entityId ?? undefined, from: from || undefined, to: to || undefined },
      });
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.ok) throw new Error(b?.message || b?.error || 'Failed');
      setReadiness(b.readiness); setFilingId(b.filingId); loadFilings();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed'); }
    finally { setPreparing(false); }
  }
  async function file() {
    if (!filingId) return;
    try {
      const res = await adminFetch(`/api/gst/file/${filingId}/mark-filed`, { method: 'POST', query: { entityId: entityId ?? undefined }, body: JSON.stringify({ arn }) });
      const b = await res.json().catch(() => ({}));
      if (!res.ok || !b.ok) throw new Error(b?.message || 'Failed');
      toast.success('Marked as filed'); setReadiness(null); setFilingId(null); setArn(''); loadFilings();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed'); }
  }

  const mine = filings.filter((f) => f.returnType === returnType);
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink-900">File {returnType}</p>
        {!readiness && <button onClick={prepare} disabled={preparing || !entityId} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-white transition hover:-translate-y-0.5 disabled:opacity-50">{preparing ? 'Checking…' : 'Prepare for filing'}</button>}
      </div>

      {readiness && (
        <div className="mt-4 space-y-3">
          <div className={`rounded-xl border p-3.5 ${readiness.canFile ? 'border-ok-200 bg-ok-50/60' : 'border-danger-200 bg-danger-50/60'}`}>
            <p className={`text-sm font-medium ${readiness.canFile ? 'text-ok-700' : 'text-danger-700'}`}>
              {readiness.canFile ? 'Ready to file.' : `${readiness.blockingErrors} blocking issue${readiness.blockingErrors !== 1 ? 's' : ''} — fix before filing.`}
            </p>
            <p className="mt-1 text-xs text-ink-600">
              {readiness.gstinMissing ? 'Entity GSTIN not set. ' : `GSTIN ${readiness.gstin}. `}
              {readiness.warnings > 0 && `${readiness.warnings} warning(s) to review. `}
            </p>
          </div>
          {readiness.canFile && (
            <>
              <p className="text-xs text-ink-500">Download the portal JSON above and upload it on the GST portal (or via your GSP). Once filed, record the ARN here:</p>
              <div className="flex flex-wrap items-center gap-2">
                <input value={arn} onChange={(e) => setArn(e.target.value.toUpperCase())} placeholder="ARN from portal" className="w-56 rounded-xl border border-ink-200 bg-white px-3 py-2 font-mono text-sm text-ink-900 focus:border-ink-300 focus-visible:outline-none" />
                <button onClick={file} className="rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50">Mark as filed</button>
              </div>
            </>
          )}
        </div>
      )}

      {mine.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">Filing history</p>
          <div className="divide-y divide-ink-100 rounded-xl border border-ink-200">
            {mine.slice(0, 6).map((f) => (
              <div key={f.id} className="flex items-center justify-between px-3.5 py-2.5 text-sm">
                <span className="text-ink-600">{f.period || '—'} · <span className="tabular-nums">₹{inr(f.taxableValue)}</span></span>
                <span className="flex items-center gap-2">
                  {f.arn && <span className="font-mono text-[11px] text-ink-400">{f.arn}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${f.status === 'filed' ? 'bg-ok-50 text-ok-600' : 'bg-ink-100 text-ink-500'}`}>{f.status}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="mt-3 text-[11px] text-ink-400">Automated e-filing needs the taxpayer’s GST-portal OTP via a GSP; this prepares & tracks the return with the portal-ready JSON.</p>
    </div>
  );
}

/* ── GSTR-1 view ────────────────────────────────────────────────────────── */

/**
 * GSTR-1 — read the return, then file it.
 *
 * Restructured from a flat stack of eight cards. The old order put the filing
 * machinery first — Download JSON top-right, then prepare, then the portal
 * steps — and left the actual numbers below the fold, under everything. A CA
 * checks the figures before filing, so the figures come first and the page now
 * reads as one sequence rather than a pile of panels.
 *
 * Blocking problems are collected into a single band instead of being repeated
 * as three separate banners saying the same thing, and they GATE the filing
 * step rather than sitting next to it as advice.
 */
function Gstr1View({ r, entityId }: { r: Returns; entityId: string | null }) {
  const g = r.gstr1; const s = g.summary;

  /* What stops this return being filed, worst first. Only genuine blockers —
     things that make the return wrong or that GSTN will reject. */
  const blockers: { title: string; detail: string; fix?: { label: string; to: string } }[] = [];
  if (!r.gstin) blockers.push({
    title: 'No GSTIN on this client',
    detail: 'A return is filed per GSTIN. Without it, interstate versus intrastate cannot be decided, so the B2CL and B2CS split is provisional.',
    fix: { label: 'Add the registration', to: '/admin/masters?tab=company' },
  });
  if (!g.fp) blockers.push({ title: 'No period selected', detail: 'Pick the month this return covers from the period bar above.' });
  if (r.unassignedDocuments) blockers.push({
    title: `${r.unassignedDocuments} document${r.unassignedDocuments > 1 ? 's' : ''} not attributed to a registration`,
    detail: 'Each document has to belong to the GSTIN it was raised under before it can be filed.',
  });
  if (g.crossCheck && !g.crossCheck.ok) blockers.push({
    title: 'Section totals do not match the HSN summary',
    detail: 'The two halves of the return disagree. GSTN validates them against each other, so this would be rejected.',
  });
  if (r.unknownHsnCodes?.length) blockers.push({
    title: `${r.unknownHsnCodes.length} HSN/SAC code${r.unknownHsnCodes.length > 1 ? 's are' : ' is'} not in the GSTN master`,
    detail: `${r.unknownHsnCodes.join(', ')} — descriptions are left blank rather than invented. Load the master, or correct the code on the ledger.`,
    fix: { label: 'HSN master', to: '/admin/masters?tab=items' },
  });

  return (
    <div className="space-y-8">
      {/* ── 1 · the return ─────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={1} title="The return" hint={`GSTIN ${r.gstin ?? '— not set'} · period ${g.fp || '— not selected'}`} />

        <div className="grid gap-3 sm:grid-cols-3">
          <Tile label="Total documents" value={String(s.totalDocs)} />
          <Tile label="Taxable value" value={`₹${inr(s.taxableValue)}`} />
          <Tile label="Total tax" value={`₹${inr(s.totalTax)}`} />
        </div>

        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Section</th><th className="px-5 py-3 text-right font-medium">Docs</th>
                <th className="px-5 py-3 text-right font-medium">Taxable value</th><th className="px-5 py-3 text-right font-medium">Tax</th>
              </tr>
            </thead>
            <tbody>
              {s.sections.map((sec) => (
                <tr key={sec.code} className={`border-b border-ink-100 last:border-0 ${sec.count === 0 ? 'opacity-40' : ''}`}>
                  <td className="px-5 py-3"><span className="font-mono text-xs uppercase text-ink-400">{sec.code}</span> <span className="ml-2 text-ink-800">{sec.label}</span></td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink-500">{sec.count}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink-800">₹{inr(sec.taxableValue)}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-ink-600">₹{inr(sec.tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <HsnSummary portal={g.portal} />
      </section>

      {/* ── 2 · what to fix ────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band
          n={2}
          title={blockers.length ? `Fix before filing — ${blockers.length}` : 'Nothing blocking'}
          hint={blockers.length ? 'GSTN validates these; the return would be wrong or rejected.' : 'No blocking problems found in this return.'}
          tone={blockers.length ? 'danger' : 'ok'}
        />
        {blockers.length === 0 ? (
          <div className="flex items-center gap-2 rounded-2xl border border-ok-200 bg-ok-50/60 px-5 py-4 text-sm text-ok-700">
            <CheckCircle2 className="h-4 w-4 shrink-0" /> Ready to file.
          </div>
        ) : (
          <div className="divide-y divide-ink-100 overflow-hidden rounded-2xl border border-danger-200 bg-white">
            {blockers.map((b) => (
              <div key={b.title} className="flex items-start gap-3 px-5 py-4">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-600" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-ink-900">{b.title}</p>
                  <p className="mt-0.5 text-sm text-ink-600">{b.detail}</p>
                </div>
                {b.fix && (
                  <Link to={b.fix.to} className="shrink-0 rounded-full border border-ink-300 bg-white px-3 py-1.5 text-xs font-semibold text-ink-700 no-underline hover:bg-ink-50">
                    {b.fix.label}
                  </Link>
                )}
              </div>
            ))}
          </div>
        )}
        <CrossCheckPanel crossCheck={g.crossCheck} />
        <DocIssueSummary portal={g.portal} unassigned={r.unassignedDocuments} />
      </section>

      {/* ── 3 · file it ────────────────────────────────────────────────── */}
      <section className="space-y-4">
        <Band n={3} title="File it" hint="Prepare the return, then send it to the portal. The JSON is the manual fallback." />
        {blockers.length > 0 && (
          <p className="rounded-xl border border-warn-200 bg-warn-50 px-4 py-2.5 text-xs text-warn-800">
            Filing is still available, but the problems above are unresolved — check them first.
          </p>
        )}
        <FilingPanel returnType="GSTR1" entityId={entityId} />
        <FileGstr1Panel entityId={entityId} period={g.fp} />
        <div className="flex items-center justify-between rounded-2xl border border-ink-200 bg-white px-5 py-4">
          <div>
            <p className="text-sm font-semibold text-ink-900">Portal JSON</p>
            <p className="mt-0.5 text-xs text-ink-500">Upload by hand on the GST portal if you would rather not file from here.</p>
          </div>
          <button
            onClick={() => downloadJson(`GSTR1_${g.gstin ?? 'entity'}_${g.fp || 'period'}.json`, g.portal)}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink-300 bg-white px-4 py-2 text-xs font-semibold text-ink-700 transition hover:bg-ink-50"
          >
            <Download className="h-3.5 w-3.5" /> Download JSON
          </button>
        </div>
      </section>
    </div>
  );
}

/** Numbered band heading — turns a stack of cards into a sequence. */
function Band({ n, title, hint, tone = 'neutral' }: { n: number; title: string; hint?: string; tone?: 'neutral' | 'danger' | 'ok' }) {
  const dot = tone === 'danger' ? 'bg-danger-600 text-white' : tone === 'ok' ? 'bg-ok-600 text-white' : 'bg-inkblack text-highlighter';
  return (
    <div className="flex items-start gap-3">
      <span className={`grid size-6 shrink-0 place-items-center rounded-full text-xs font-bold ${dot}`}>{n}</span>
      <div className="min-w-0">
        <h3 className="text-base font-semibold leading-6 text-ink-900">{title}</h3>
        {hint && <p className="mt-0.5 text-xs text-ink-500">{hint}</p>}
      </div>
    </div>
  );
}

/* ── GSTR-3B view ───────────────────────────────────────────────────────── */

function Gstr3bView({ r, entityId }: { r: Returns; entityId: string | null }) {
  const g = r.gstr3b;
  const line = (label: string, t: TaxLine, showTaxable = true) => (
    <tr className="border-b border-ink-100 last:border-0">
      <td className="px-5 py-3 text-ink-800">{label}</td>
      <td className="px-5 py-3 text-right tabular-nums text-ink-800">{showTaxable ? `₹${inr(t.taxableValue)}` : '—'}</td>
      <td className="px-5 py-3 text-right tabular-nums text-ink-600">{inr(t.igst)}</td>
      <td className="px-5 py-3 text-right tabular-nums text-ink-600">{inr(t.cgst)}</td>
      <td className="px-5 py-3 text-right tabular-nums text-ink-600">{inr(t.sgst)}</td>
    </tr>
  );
  return (
    <div className="space-y-6">
      <ReturnHeader title="GSTR-3B" subtitle="Summary return" gstin={r.gstin} fp={g.fp} onDownload={() => downloadJson(`GSTR3B_${g.gstin ?? 'entity'}_${g.fp || 'period'}.json`, g.portal ?? g)} />
      <FilingPanel returnType="GSTR3B" entityId={entityId} />
      <ManualEntryPanel items={g.needsManualEntry} />
      <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-400">
              <th className="px-5 py-3 font-medium">Nature of supply</th><th className="px-5 py-3 text-right font-medium">Taxable</th>
              <th className="px-5 py-3 text-right font-medium">IGST</th><th className="px-5 py-3 text-right font-medium">CGST</th><th className="px-5 py-3 text-right font-medium">SGST</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-ink-50/70"><td colSpan={5} className="px-5 py-2 text-xs font-semibold uppercase tracking-wide text-ink-400">3.1 Outward supplies</td></tr>
            {line('(a) Taxable (other than zero/nil-rated)', g.outward.taxableOutward)}
            {line('(b) Zero-rated (exports + SEZ)', g.outward.zeroRated)}
            {line('(c) Nil-rated / exempt', g.outward.nilExempt)}
            <tr className="bg-ink-50/70"><td colSpan={5} className="px-5 py-2 text-xs font-semibold uppercase tracking-wide text-ink-400">4 Eligible ITC</td></tr>
            {line('(A) ITC available (from purchases)', g.itc, false)}
            <tr className="bg-highlighter-40/50 font-semibold"><td className="px-5 py-3 text-ink-900">Net tax payable (indicative)</td><td className="px-5 py-3 text-right text-ink-400">—</td>
              <td className="px-5 py-3 text-right tabular-nums text-ink-900">{inr(g.netTaxPayable.igst)}</td>
              <td className="px-5 py-3 text-right tabular-nums text-ink-900">{inr(g.netTaxPayable.cgst)}</td>
              <td className="px-5 py-3 text-right tabular-nums text-ink-900">{inr(g.netTaxPayable.sgst)}</td></tr>
          </tbody>
        </table>
      </div>
      <p className="text-xs text-ink-500">Net payable is indicative (output tax − ITC), before cash/credit ledger adjustments and reverse charge.</p>
    </div>
  );
}

/* ── shared bits ────────────────────────────────────────────────────────── */

function ReturnHeader({ title, subtitle, gstin, fp, onDownload }: { title: string; subtitle: string; gstin: string | null; fp: string; onDownload: () => void }) {
  return (
    <div className="flex items-center justify-between rounded-2xl border border-ink-200 bg-white p-4">
      <div>
        <p className="text-base font-semibold text-ink-900">{title} <span className="ml-1 text-sm font-normal text-ink-400">· {subtitle}</span></p>
        <p className="mt-0.5 text-xs text-ink-500">GSTIN {gstin ?? '— (not set on entity)'} · Period {fp || '— (select a period)'}</p>
      </div>
      <button onClick={onDownload} className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-4 py-2 text-xs font-semibold text-white transition hover:-translate-y-0.5">
        <Download className="h-3.5 w-3.5" /> Download JSON
      </button>
    </div>
  );
}

function Tab({ active, onClick, icon: Icon, label }: { active: boolean; onClick: () => void; icon: typeof ArrowUpRight; label: string }) {
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium transition ${active ? 'bg-inkblack text-white' : 'text-ink-600 hover:bg-ink-50'}`}>
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warn' | 'ok' }) {
  const ring = tone === 'warn' ? 'border-warn-200 bg-warn-50/50' : tone === 'ok' ? 'border-ok-200 bg-ok-50/50' : 'border-ink-200 bg-white';
  return (
    <div className={`rounded-2xl border p-4 ${ring}`}>
      <p className="text-xs font-medium text-ink-500">{label}</p>
      <p className="mt-1 text-xl font-semibold tabular-nums text-ink-900">{value}</p>
      {sub && <p className="mt-1 text-[11px] text-ink-500">{sub}</p>}
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: { label: string; count: number; taxable: number; tax: number }[] }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{title}</p>
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => (
            <tr key={r.label} className="border-b border-ink-100 last:border-0">
              <td className="py-2 font-medium text-ink-700">{r.label}</td>
              <td className="py-2 text-right text-xs text-ink-400">{r.count} docs</td>
              <td className="py-2 text-right tabular-nums text-ink-700">₹{inr(r.taxable)}</td>
              <td className="py-2 pl-3 text-right tabular-nums text-ink-500">₹{inr(r.tax)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Empty({ label }: { label: string }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white px-6 py-16 text-center">
      <h3 className="text-lg font-semibold text-ink-900">No {label} documents</h3>
      <p className="mt-1.5 text-sm text-ink-600">Import Tally data for this entity and period to see its GST data.</p>
    </div>
  );
}
